/**
 * Debug Styles for FirstDistro SDK
 * 
 * Shared CSS styles for debug badge and overlay.
 * Returns CSS strings for inline use in shadow DOM.
 */

/**
 * Shared styles (CSS reset, colors, fonts)
 */
export function getSharedStyles(): string {
  return `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    
    :host {
      --fd-bg: #ffffff;
      --fd-bg-hover: #f5f5f5;
      --fd-text: #1a1a1a;
      --fd-text-muted: #666666;
      --fd-border: #e5e5e5;
      --fd-primary: #2563eb;
      --fd-primary-hover: #1d4ed8;
      --fd-success: #22c55e;
      --fd-warning: #f59e0b;
      --fd-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      --fd-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      --fd-radius: 8px;
      --fd-radius-sm: 4px;
    }
    
    @media (prefers-color-scheme: dark) {
      :host {
        --fd-bg: #1f1f1f;
        --fd-bg-hover: #2a2a2a;
        --fd-text: #f5f5f5;
        --fd-text-muted: #a0a0a0;
        --fd-border: #333333;
        --fd-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      }
    }
  `;
}

/**
 * Badge styles
 */
export function getBadgeStyles(): string {
  return `
    ${getSharedStyles()}
    
    .fd-badge {
      position: fixed;
      bottom: 16px;
      right: 16px;
      z-index: 999999;
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 6px 10px;
      background: var(--fd-bg);
      border: 1px solid var(--fd-border);
      border-radius: var(--fd-radius);
      box-shadow: var(--fd-shadow);
      font-family: var(--fd-font);
      font-size: 12px;
      font-weight: 500;
      color: var(--fd-text);
      cursor: pointer;
      user-select: none;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    
    .fd-badge:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
    
    .fd-badge:active {
      transform: translateY(0);
    }
    
    .fd-badge-text {
      font-weight: 600;
      color: var(--fd-primary);
    }
    
    .fd-badge-status {
      font-size: 10px;
    }
    
    .fd-badge-count {
      min-width: 18px;
      padding: 2px 6px;
      background: var(--fd-bg-hover);
      border-radius: var(--fd-radius-sm);
      font-size: 11px;
      font-weight: 600;
      text-align: center;
    }
  `;
}

/**
 * Overlay styles
 */
export function getOverlayStyles(): string {
  return `
    ${getSharedStyles()}
    
    .fd-overlay {
      position: fixed;
      bottom: 60px;
      right: 16px;
      z-index: 999998;
      width: 280px;
      background: var(--fd-bg);
      border: 1px solid var(--fd-border);
      border-radius: var(--fd-radius);
      box-shadow: var(--fd-shadow);
      font-family: var(--fd-font);
      font-size: 13px;
      color: var(--fd-text);
      overflow: hidden;
    }
    
    .fd-overlay.fd-hidden {
      display: none;
    }
    
    .fd-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: var(--fd-bg-hover);
      border-bottom: 1px solid var(--fd-border);
      cursor: move;
    }
    
    .fd-status-indicator {
      font-size: 12px;
    }
    
    .fd-title {
      flex: 1;
      font-weight: 600;
      font-size: 13px;
    }
    
    .fd-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: transparent;
      color: var(--fd-text-muted);
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      border-radius: var(--fd-radius-sm);
      transition: background 0.15s ease, color 0.15s ease;
    }
    
    .fd-close:hover {
      background: var(--fd-border);
      color: var(--fd-text);
    }
    
    .fd-body {
      padding: 14px;
    }
    
    .fd-section {
      margin-bottom: 12px;
    }
    
    .fd-section:last-child {
      margin-bottom: 0;
    }
    
    .fd-label {
      font-size: 11px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--fd-text-muted);
      margin-bottom: 4px;
    }
    
    .fd-value {
      font-size: 13px;
      font-weight: 500;
      color: var(--fd-text);
      word-break: break-word;
    }
    
    .fd-divider {
      height: 1px;
      background: var(--fd-border);
      margin: 14px 0;
    }
    
    .fd-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .fd-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 8px 12px;
      border: none;
      border-radius: var(--fd-radius-sm);
      font-family: var(--fd-font);
      font-size: 12px;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    
    .fd-btn-link {
      background: var(--fd-primary);
      color: #ffffff;
    }
    
    .fd-btn-link:hover {
      background: var(--fd-primary-hover);
    }
  `;
}

export default {
  getSharedStyles,
  getBadgeStyles,
  getOverlayStyles,
};
